import { useCallback, useState } from 'react';

export interface ChatInputProps {
  isEnabled: boolean;
  maxLength: number;
  /**
   * メッセージ送信ハンドラ。送信の成否を boolean で返す
   * （false の場合は入力内容を保持し、再送信できるようにする。STU-6対応）。
   * boolean を返さない実装（void）は成功として扱う
   */
  onSubmit: (message: string) => Promise<boolean | void> | boolean | void;
  placeholder?: string;
  inputId?: string | undefined;
  buttonId?: string | undefined;
}

/**
 * ChatInput - Text input component for chat messages.
 *
 * Features:
 * - Submit button with enabled/disabled states
 * - Uses existing .chat-input styles from viewer.css
 * - Enterキーでの送信を無効化（ボタンクリックのみ）
 * - 送信失敗時は入力内容を保持して再送信を可能にする（STU-6対応）
 * - Full accessibility support (ARIA labels, keyboard navigation)
 *
 * Validates: Requirements 5.3, 5.4, 5.5, 5.6, 10.1, 10.2
 */
export function ChatInput({
  isEnabled,
  maxLength,
  onSubmit,
  placeholder = 'メッセージを入力...',
  inputId,
  buttonId,
}: ChatInputProps) {
  const [inputValue, setInputValue] = useState('');
  // 送信処理中フラグ（連打による二重送信を防ぎ、送信完了までボタンを無効化する）
  const [isSending, setIsSending] = useState(false);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    [],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        // Do nothing - Enter should not trigger submission
      }
    },
    [],
  );

  const handleButtonClick = useCallback(async () => {
    const trimmedMessage = inputValue.trim();
    if (!trimmedMessage || !isEnabled || isSending) return;

    setIsSending(true);
    try {
      const result = await onSubmit(trimmedMessage);
      // 送信成功時のみ入力欄をクリアする。失敗（false）時は入力内容を保持し、
      // 受講生が再送信できるようにする（STU-6対応）
      if (result !== false) {
        setInputValue('');
      }
    } finally {
      setIsSending(false);
    }
  }, [inputValue, isEnabled, isSending, onSubmit]);

  return (
    <div className="chat-input">
      <input
        {...(inputId ? { id: inputId } : {})}
        type="text"
        placeholder={isEnabled ? placeholder : 'チャットは現在利用できません'}
        maxLength={maxLength}
        disabled={!isEnabled}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />
      <button
        {...(buttonId ? { id: buttonId } : {})}
        type="button"
        disabled={!isEnabled || !inputValue.trim() || isSending}
        onClick={handleButtonClick}
      >
        送信
      </button>
    </div>
  );
}
